import React, { useEffect, useState } from 'react'
import { IntegrationCredentialsContent } from '@features/nbee/IntegrationCredentialsContent'
import { useParams } from 'react-router-dom'
import { IntegrationCredentialParams } from 'LeadsBridgeApp'
import { usePostNewIntegration } from '@app/api/postNewIntegration'
import {
  makeIntegrationFormInitialValues,
  transformIntegrationFormValuesToApiSaveRequestBody,
} from '@features/nbee/IntegrationCredentialsContent/AppAuthCredentialForm/utils'
import { IntegrationFormValues } from 'Nbee'
import { ApiErrorResponse } from 'BackendApi'
import { FormikHelpers } from 'formik'
import { useAppDispatch } from '@app/store/hooks'
import { parseApiError } from '@app/api/utils/error'
import { sendToast } from '@app/store/actions/ApplicationConfigurationActions'
import { Loader } from '@components/Basic/Loader'
import { sendIntegrationDataToParentWindow } from '@features/nbee/IntegrationCredentialsContent/utils'
import { Message } from '@components/Basic/Message'
import { useGetAppById } from '@app/api/getAppById'
import { useRetrieveAppCredentials } from '@app/api/postRetrieveAppCredentials'
import { useTestIntegrationCredentialsById } from '@app/api/testIntegrationCredentialsById'
import { useTranslation } from 'react-i18next'
import { useUpdateIntegration } from '@app/api/updateIntegration'
import { trackEvent } from '@app/dataTracking'

// This page is supposed to be always opened inside a popup window with reduced dimensions
// In future we might add an additional check and prevent the component to render if `window.opener` is not set
// this means that this component has not been mounted in a page generated from a parent window
export const IntegrationAuthScreen: React.FC = () => {
  const { appId } = useParams<IntegrationCredentialParams>()
  const { t } = useTranslation()
  const [newIntegration, setNewIntegration] =
    useState<{ id: number; name: string }>()
  const appIdNumber = parseInt(appId, 10)
  // this will be used to keep the loading spinner while sending data to parent and modal is about to be closed
  const [isAboutToClosingModal, setIsAboutToClosingModal] = useState(false)
  const [showManualCloseMessage, setShowManualCloseMessage] = useState(false)

  const dispatch = useAppDispatch()

  const {
    data: appData,
    isLoading: isLoadingApp,
    error: errorApiGetApp,
  } = useGetAppById(appId)

  const {
    data: appAuthSchemaResponse,
    mutate: retrieveAuthSchema,
    isLoading: isLoadingAppAuthSchema,
    error: errorApiAppAuthSchema,
  } = useRetrieveAppCredentials()

  const {
    mutate: saveNewIntegration,
    isLoading: isSavingNewIntegration,
    error: integrationSaveApiError,
    data: savedIntegration,
  } = usePostNewIntegration()

  const {
    mutate: updateIntegration,
    isLoading: isUpdatingIntegration,
    error: integrationUpdateApiError,
    data: updatedIntegrationData,
  } = useUpdateIntegration()

  // if error we notify the user
  useEffect(() => {
    const error = integrationUpdateApiError || integrationSaveApiError
    if (error) {
      const errorMessage = parseApiError(error).message
      dispatch(
        sendToast({
          title: 'Error',
          messages: [errorMessage],
          color: 'negative',
        })
      )
    }
  }, [integrationUpdateApiError, integrationSaveApiError])

  // retrieving auth schema on mount
  useEffect(() => {
    retrieveAuthSchema({
      appId: appIdNumber,
      settings: [],
    })
  }, [appIdNumber])

  const {
    data: testIntegrationResults,
    error: integrationTestError,
    isLoading: isCheckingValidIntegration,
    isRefetching: isReCheckingValidIntegration,
    refetch: reTestValidIntegration,
  } = useTestIntegrationCredentialsById(newIntegration?.id)

  // we build the full auth schema by merging existing data into the full app auth schema
  // we also consider `optimisticIntegrationData` as data just saved, so in case of test error
  // we fill initial state with the new saved data
  const optimisticIntegrationData = savedIntegration?.data?.integration
  const appAuthSchema = appAuthSchemaResponse?.data?.credentials.fields || []
  const isOauthUri = appAuthSchemaResponse?.data?.credentials.type === 'oauth2'
  const initialValues = appData
    ? makeIntegrationFormInitialValues(
        appAuthSchema,
        appData.name,
        optimisticIntegrationData
      )
    : null

  // handle success save/updated
  useEffect(() => {
    const isSuccessResponse =
      updatedIntegrationData || savedIntegration?.data?.integration
    if (isSuccessResponse) {
      const integrationId = isSuccessResponse.id
      const integrationName = isSuccessResponse.name
      if (newIntegration?.id) {
        // in case `newIntegration` is already set the `useTestIntegrationCredentialsById` hook
        // will not re-run, so re refetch data manually
        reTestValidIntegration()
      }
      setNewIntegration({ id: integrationId, name: integrationName })
    }
  }, [updatedIntegrationData, savedIntegration])

  // on mount events
  const isReady = Boolean(appData && initialValues && appAuthSchemaResponse)
  useEffect(() => {
    if (isReady) {
      trackEvent({
        eventName: 'NewIntegrationShown',
        feature: 'NBEE',
        step: 'Integration',
        params: {
          appId: appData?.id,
          appName: appData?.name,
          oauth: isOauthUri,
        },
      })
    }
  }, [isReady])

  const trackEventError = (
    apiError: ApiErrorResponse,
    formValues: IntegrationFormValues
  ) => {
    const parsedError = parseApiError(apiError)
    trackEvent({
      eventName: 'UnexpectedErrorThrown',
      feature: 'NBEE',
      step: 'Apps',
      params: {
        appId: parseInt(appId, 10),
        appName: appData?.name,
        integrationId: newIntegration?.id,
        integrationName: formValues.name,
        custom: {
          errorCode: parsedError.code,
          errorDescription: parsedError.message,
        },
      },
      sendEventToIntercom: true,
    })
  }

  const saveIntegration = (
    formValues: IntegrationFormValues,
    formikHelpers: FormikHelpers<IntegrationFormValues>
  ) => {
    formikHelpers.setSubmitting(true)

    // in we already saved the integration and we received an error
    // next action will be an updated and not a saved new
    // we know this by checking newIntegration object
    if (newIntegration?.id) {
      updateIntegration(
        {
          integrationId: `${newIntegration.id}`,
          requestBody: transformIntegrationFormValuesToApiSaveRequestBody(
            formValues,
            appIdNumber
          ),
        },
        {
          onError: (apiError) => {
            formikHelpers.setSubmitting(false)
            trackEventError(apiError, formValues)
          },
        }
      )

      return
    }

    saveNewIntegration(
      {
        requestBody: transformIntegrationFormValuesToApiSaveRequestBody(
          formValues,
          appIdNumber
        ),
      },
      {
        onError: (apiError) => {
          formikHelpers.setSubmitting(false)
          trackEventError(apiError, formValues)
        },
      }
    )
  }

  // Proceeding when test is successful
  useEffect(() => {
    if (testIntegrationResults?.data?.result && newIntegration) {
      setIsAboutToClosingModal(true)

      if (!isOauthUri) {
        // when test is OK and we are not in oauth flow, before sending the SaveClicked Event, we need to send
        // the `AuthorizeSuccess` so we can simulate the same events order of the oauth integration
        trackEvent({
          eventName: 'AuthorizeSuccess',
          feature: 'NBEE',
          step: 'Integration',
          params: {
            appId: appData?.id,
            appName: appData?.name,
            integrationId: newIntegration.id,
            integrationName: newIntegration.name,
            oauth: false,
          },
        })
      }

      trackEvent({
        eventName: 'SaveClicked',
        feature: 'NBEE',
        step: 'Integration',
        params: {
          appId: appData?.id,
          appName: appData?.name,
          integrationId: newIntegration.id,
          integrationName: newIntegration.name,
          oauth: isOauthUri,
        },
      })

      const sentToParent = sendIntegrationDataToParentWindow(newIntegration.id, newIntegration.name)
      if (!sentToParent) {
        setShowManualCloseMessage(true)
      }
    }
  }, [testIntegrationResults])

  // In case of test failure
  useEffect(() => {
    // in case test fails we send the `AuthorizeFailure` tracking event only if is not an oauth flow
    // for oauth integrations we send this event during the oauth flow, insice IntegrationAuthCallback component
    if (integrationTestError && !isOauthUri) {
      const parsedTestError = parseApiError(integrationTestError)
      trackEvent({
        eventName: 'AuthorizeFailure',
        feature: 'NBEE',
        step: 'Integration',
        params: {
          appId: appData?.id,
          appName: appData?.name,
          integrationId: newIntegration?.id,
          integrationName: newIntegration?.name,
          oauth: false,
          custom: {
            errorCode: parsedTestError.code,
            errorDescription: parsedTestError.message,
          },
        },
        sendEventToIntercom: true,
      })
    }
  }, [integrationTestError])

  const isLoadingAll =
    isLoadingApp ||
    isLoadingAppAuthSchema ||
    isCheckingValidIntegration ||
    isReCheckingValidIntegration ||
    !initialValues ||
    (isAboutToClosingModal && !showManualCloseMessage)
  const apiError = errorApiAppAuthSchema || errorApiGetApp

  if (!appId) {
    return <div>Missing Parameter</div>
  }

  if (showManualCloseMessage) {
    return (
      <div style={{ margin: '2rem', textAlign: 'center' }}>
        <Message $status={'success'}>
          {t('integrationAuthPopup.manualCloseMessage')}
        </Message>
      </div>
    )
  }

  return isLoadingAll ? (
    <div></div>
  ) : appData && initialValues ? (
    <IntegrationCredentialsContent
      appId={appIdNumber}
      appName={appData.name}
      appLogoUri={appData.logoUriSmall}
      isSavingApi={isUpdatingIntegration || isSavingNewIntegration}
      onSubmit={saveIntegration}
      isOauthUri={isOauthUri}
      initialValues={initialValues}
      isNotValidIntegrationErrorMessage={
        integrationTestError
          ? parseApiError(integrationTestError).message
          : testIntegrationResults?.data?.result === false
          ? testIntegrationResults.data.message ||
            t('api.error.genericNoMessageReceived')
          : undefined
      }
    />
  ) : (
    <Message $status={'error'}>
      {apiError ? parseApiError(apiError).message : 'Missing data'}
    </Message>
  )
}
